import { useMemo } from "react";
import type { FliptClient } from "@flipt-io/flipt-client-js";
import { useFliptSelector } from "@flipt-io/flipt-client-react";
import useUser from "~/contexts/user";
import { FliptFlagKey } from "~/router/constants";

export interface FliptFeature {
  enabled: boolean;
  isLoading: boolean;
}

/**
 * Evaluate a Flipt boolean flag for the current user.
 *
 * Reports `isLoading` until the client *and* the caller's identity have settled.
 * Rollouts match on `entityId`, so a result computed before `/me` resolves is an
 * answer about the empty string, and a route gate acting on it 404s the user.
 */
export const useFliptFeature = (flagKey: FliptFlagKey): FliptFeature => {
  const user = useUser();

  const isFliptPending = useFliptSelector(
    (client: FliptClient | null, isLoading: boolean, _error: Error | null) => !client || isLoading
  );

  const enabled = useFliptSelector(
    (client: FliptClient | null, isLoading: boolean, error: Error | null) => {
      if (!client || isLoading) {
        return false;
      }
      if (error) {
        return false;
      }
      try {
        return client.evaluateBoolean({
          flagKey,
          entityId: user?.email ?? "",
          context: {},
        }).enabled;
      } catch (e) {
        console.error(`Error evaluating boolean flag ${flagKey}:`, e);
        return false;
      }
    }
  );

  return useMemo(
    () => ({ isLoading: isFliptPending || !user, enabled }),
    [isFliptPending, enabled, user]
  );
};
